/** Share authoritative Builder gates between the workspace pane and chat. */
import { readResponse } from '@web/api/read'
import { useEffect, useState } from 'react'

export type BuilderGate = {
  revision: number
  phase: number
  current_phase: string
  status: string
  plan?: unknown
  progress?: unknown
  gate?: BuilderDecisionGate | null
}

export type BuilderDecisionChoice = {
  id: string
  label: string
  message: string
  primary: boolean
}

export type BuilderDecisionGate = {
  kind: 'approval' | 'preview' | 'publish'
  question: string
  choices: BuilderDecisionChoice[]
}

export const BUILDER_WORKFLOW_EVENT = 'bridge:builder-workflow'

export function publishBuilderWorkflow(
  bindingId: string,
  workflow: BuilderGate,
) {
  window.dispatchEvent(
    new CustomEvent(BUILDER_WORKFLOW_EVENT, {
      detail: { bindingId, workflow },
    }),
  )
}

export function useBuilderWorkflow(bindingId?: string | null) {
  const [saved, setSaved] = useState<{
    bindingId: string
    workflow: BuilderGate
  } | null>(null)
  useEffect(() => {
    if (!bindingId) return
    const controller = new AbortController()
    let pending = false
    const accept = (workflow: BuilderGate | undefined) => {
      if (
        !workflow ||
        !Number.isInteger(workflow.revision) ||
        controller.signal.aborted
      )
        return
      setSaved((previous) =>
        previous?.bindingId === bindingId &&
        previous.workflow.revision > workflow.revision
          ? previous
          : { bindingId, workflow },
      )
    }
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return
      pending = true
      try {
        const response = await readResponse(
          `/v1/apps/bindings/${encodeURIComponent(bindingId)}/state`,
          { signal: controller.signal },
        )
        if (response.ok) accept((await response.json()).workflow)
        else if ([401, 403, 404].includes(response.status)) setSaved(null)
      } catch {
        // A fresh read at click time still protects a temporarily disconnected gate.
      } finally {
        pending = false
      }
    }
    const receive = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.bindingId === bindingId) accept(detail.workflow)
    }
    void refresh()
    const timer = setInterval(() => void refresh(), 3000)
    window.addEventListener(BUILDER_WORKFLOW_EVENT, receive)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      controller.abort()
      clearInterval(timer)
      window.removeEventListener(BUILDER_WORKFLOW_EVENT, receive)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [bindingId])
  return saved && saved.bindingId === bindingId ? saved.workflow : null
}
